// frontend/src/widgets/AdvancedTaskListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-07
// Updated:      2026-10-22 (BST; mobile card visibility and sizing)
//
// Description:
//   Tenant-safe operational list of runtime workflow tasks using the unified planner projection.
//   Desktop uses a configurable sortable table; mobile/PWA uses the same selected column set
//   rendered as touch-friendly task cards.
//
// Change Log:
//   2026-10-07: Initial Tasks-page / Jobs-page cross-surface runtime task widget.
//   2026-10-14 09:54 BST:
//     - Standardised list behaviour against the established JobListWidget pattern.
//     - Added persistent tenant/user-scoped Column Chooser.
//     - Added Task ID as a first-class selectable/default-visible column for Data Integrity work.
//     - Added click-to-sort desktop headers and mobile Sort By/direction controls.
//     - Added desktop drag/drop column ordering and touch-safe up/down ordering in chooser.
//     - Preserved existing task filters, task modal launch event, dashboard refresh hooks,
//       responsive mobile cards and theme accent integration.
//     - No ReactGridLayout host-height mutation is performed by this widget.
//   2026-10-14 10:54 BST:
//     - Corrected presentation to mirror the supplied JobListWidget reference exactly.
//     - Removed the duplicate internal widget-title bar.
//     - Moved search/filter/Column Chooser/Refresh into the compact accent toolbar.
//     - Column Chooser now uses the JobListWidget desktop layout: plain checkbox grid;
//       move controls are mobile-only.
//     - Desktop table spacing/header styling now matches JobListWidget.
//     - Task-specific data/filtering and Task Modal launch behaviour are preserved.
//   2026-10-22:
//     - Explicit mobile card text colours and stable labelled rows.
//     - One complete card per mobile scroll, measured from rendered content.
//     - Restore and resize matching fixed-height mobile widget host.
//     - Desktop table, task events, and stored preferences unchanged.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import ModalWrapper from "../components/ModalWrapper";

const SCHEMA_VERSION = 1;

const normaliseStoragePart = (value, fallback) => {
  const raw = String(value ?? "").trim();
  return raw || fallback;
};

const storageKey = (userId, tenantId, mineOnly) =>
  `vision_task_columns_v${SCHEMA_VERSION}_${tenantId}_${userId}_${mineOnly ? "mine" : "all"}`;

const ALL_COLUMNS = [
  { key: "task_id", label: "Task ID" },
  { key: "title", label: "Task" },
  { key: "task_ref", label: "Task Ref" },
  { key: "task_key", label: "Task Key" },
  { key: "job_id", label: "Job ID" },
  { key: "job_ref", label: "Job" },
  { key: "work_package_id", label: "Work Package ID" },
  { key: "resource_id", label: "Resource" },
  { key: "status", label: "Status" },
  { key: "execution_state", label: "Execution State" },
  { key: "starts_at", label: "Scheduled Start" },
  { key: "ends_at", label: "Scheduled End" },
  { key: "week_commencing", label: "Week Commencing" },
];

const DEFAULT_VISIBLE_ORDER = [
  "task_id",
  "title",
  "task_ref",
  "job_ref",
  "resource_id",
  "execution_state",
  "starts_at",
];

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB");
};

const formatDateOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const openTask = (row) => row.item_type === "task_list"
  ? window.dispatchEvent(new CustomEvent("vision:open-task-list", { detail: { taskListId: row.task_list_id || row.id, jobId: row.job_id } }))
  :
  window.dispatchEvent(
    new CustomEvent("vision:open-task", {
      detail: {
        jobId: row.job_id,
        workPackageId: row.work_package_id,
        taskId: row.task_id || row.id,
      },
    })
  );

const ColumnChooserModal = ({
  open,
  onClose,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const moveColumn = (key, direction) => {
    setOrder((previous) => {
      const from = previous.indexOf(key);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= previous.length) return previous;
      const next = previous.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="text-sm text-gray-600 mb-3">
        Tick the columns to display. Drag desktop table headers to reorder them, or use the
        up/down controls here on mobile.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] md:max-h-80 overflow-auto pr-1 md:pr-2">
        {order.map((key, index) => {
          const column = ALL_COLUMNS.find((candidate) => candidate.key === key);
          if (!column) return null;
          return (
            <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-2 md:border-0 md:rounded-none md:p-0">
              <label className="flex min-w-0 flex-1 items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() =>
                    setVisibilityMap((previous) => ({
                      ...previous,
                      [key]: !previous[key],
                    }))
                  }
                  className="h-5 w-5 shrink-0 md:h-auto md:w-auto"
                />
                <span className="truncate">{column.label}</span>
              </label>
              <div className="flex shrink-0 gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={index === 0}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${column.label} up`}
                  title={`Move ${column.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={index === order.length - 1}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${column.label} down`}
                  title={`Move ${column.label} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={resetToDefault}
          className="min-h-[44px] rounded bg-btnGeneralNormal px-3 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
        >
          Reset to Default
        </button>
        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] rounded bg-btnActionNormal px-3 py-2 text-sm font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  return createPortal(content, document.getElementById("modal-root") || document.body);
};

const AdvancedTaskListWidget = ({ mineOnly = false }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sortField, setSortField] = useState("task_id");
  const [ascending, setAscending] = useState(true);
  const [chooserOpen, setChooserOpen] = useState(false);
  const dragIndexRef = useRef(null);
  const widgetRootRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const mobileViewportRef = useRef(null);
  const firstMobileCardRef = useRef(null);

  const authUserId = useMemo(
    () => normaliseStoragePart(user?.id ?? user?.user_id ?? user?.username ?? user?.email, "unknownUser"),
    [user?.id, user?.user_id, user?.username, user?.email]
  );
  const authTenantId = useMemo(
    () => normaliseStoragePart(user?.tenant_id ?? user?.tenantId, "unknownTenant"),
    [user?.tenant_id, user?.tenantId]
  );
  const persistKey = useMemo(
    () => storageKey(authUserId, authTenantId, mineOnly),
    [authUserId, authTenantId, mineOnly]
  );

  const makeDefaultVisibility = useCallback(() => {
    const map = {};
    ALL_COLUMNS.forEach((column) => {
      map[column.key] = DEFAULT_VISIBLE_ORDER.includes(column.key);
    });
    return map;
  }, []);

  const makeDefaultOrder = useCallback(() => {
    const rest = ALL_COLUMNS.map((column) => column.key).filter(
      (key) => !DEFAULT_VISIBLE_ORDER.includes(key)
    );
    return [...DEFAULT_VISIBLE_ORDER, ...rest];
  }, []);

  const [visibilityMap, setVisibilityMap] = useState(makeDefaultVisibility);
  const [order, setOrder] = useState(makeDefaultOrder);

  useEffect(() => {
    if (authUserId === "unknownUser" || authTenantId === "unknownTenant") return;
    try {
      const raw = localStorage.getItem(persistKey);
      const parsed = raw ? JSON.parse(raw) : null;
      const savedOrder = Array.isArray(parsed?.order) ? parsed.order : [];
      const savedVisibility = parsed?.visibilityMap || {};
      const nextOrder = savedOrder.length
        ? [
            ...savedOrder.filter((key) => ALL_COLUMNS.some((column) => column.key === key)),
            ...ALL_COLUMNS.map((column) => column.key).filter((key) => !savedOrder.includes(key)),
          ]
        : makeDefaultOrder();
      const nextVisibility = makeDefaultVisibility();
      ALL_COLUMNS.forEach((column) => {
        if (typeof savedVisibility[column.key] === "boolean") {
          nextVisibility[column.key] = savedVisibility[column.key];
        }
      });
      setOrder(nextOrder);
      setVisibilityMap(nextVisibility);
    } catch {
      setOrder(makeDefaultOrder());
      setVisibilityMap(makeDefaultVisibility());
    }
  }, [authUserId, authTenantId, persistKey, makeDefaultOrder, makeDefaultVisibility]);

  useEffect(() => {
    if (authUserId === "unknownUser" || authTenantId === "unknownTenant") return;
    localStorage.setItem(
      persistKey,
      JSON.stringify({ v: SCHEMA_VERSION, order, visibilityMap })
    );
  }, [authUserId, authTenantId, persistKey, order, visibilityMap]);

  const visibleKeys = useMemo(
    () => order.filter((key) => visibilityMap[key]),
    [order, visibilityMap]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/planner/work-items", {
        baseURL: "/api",
        params: { include_jobs: false, include_tasks: true },
      });
      setRows((data?.items || []).filter((item) => item.item_type === "task_list" || (item.item_type === "task" && !item.task_list_id && item.task_type !== "system_event")));
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unable to load runtime tasks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener("dashboard:refresh", refresh);
    window.addEventListener("vision:tasks-changed", refresh);
    return () => {
      window.removeEventListener("dashboard:refresh", refresh);
      window.removeEventListener("vision:tasks-changed", refresh);
    };
  }, [load]);

  const statuses = useMemo(
    () => Array.from(new Set(rows.map((row) => row.status).filter(Boolean))).sort(),
    [rows]
  );

  const getCellValue = useCallback((row, key) => {
    if (key === "task_id") return row.item_type === "task_list" ? `List ${row.id}` : row.task_id ?? row.id ?? "";
    if (key === "title" && row.item_type === "task_list") return `${row.title} (${row.task_count || 0} tasks)`;
    if (key === "job_ref") return row.job_ref || row.parent_job_ref || (row.job_id ? `Job ${row.job_id}` : "");
    if (key === "resource_id") return [row.resource_id, row.co_resource_id].filter(Boolean).map(id => `Resource ${id}`).join(" / ") || "Unassigned";
    if (key === "status" || key === "execution_state") {
      return String(row[key] || "").replace(/_/g, " ");
    }
    if (key === "starts_at" || key === "ends_at") return formatDateTime(row[key]);
    if (key === "week_commencing") return formatDateOnly(row[key]);
    return row[key] ?? "";
  }, []);

  const filteredAndSorted = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (status && row.status !== status) return false;
      if (mineOnly && !row.resource_id) return false;
      if (!query) return true;
      return ALL_COLUMNS.some((column) =>
        String(getCellValue(row, column.key) ?? "").toLowerCase().includes(query)
      );
    });

    return filtered.slice().sort((a, b) => {
      const aValue = getCellValue(a, sortField);
      const bValue = getCellValue(b, sortField);
      const aNumber = Number(aValue);
      const bNumber = Number(bValue);
      let comparison;
      if (aValue !== "" && bValue !== "" && Number.isFinite(aNumber) && Number.isFinite(bNumber)) {
        comparison = aNumber - bNumber;
      } else {
        comparison = String(aValue ?? "").localeCompare(String(bValue ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return ascending ? comparison : -comparison;
    });
  }, [rows, status, mineOnly, search, sortField, ascending, getCellValue]);

  // Match the Job List mobile sizing contract: one complete record per scroll.
  // Restore any prior host override before measuring the natural content.
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const root = widgetRootRef.current;
    const header = mobileHeaderRef.current;
    const viewport = mobileViewportRef.current;
    if (!root || !header || !viewport) return undefined;

    const media = window.matchMedia("(max-width: 767px)");
    let frame = 0;
    let hostRecord = null;
    const restoreHost = () => {
      if (!hostRecord) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };
    const findHost = () => {
      const width = root.getBoundingClientRect().width;
      let node = root.parentElement;
      let depth = 0;
      while (node && node !== document.body && depth < 8) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style.height || "").trim();
        if (Math.abs(rect.width - width) < 90 && inlineHeight &&
            !["auto", "100%", "100vh"].includes(inlineHeight)) return node;
        node = node.parentElement;
        depth += 1;
      }
      return null;
    };
    const measure = () => {
      cancelAnimationFrame(frame);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
      if (!media.matches) return;
      frame = requestAnimationFrame(() => {
        const card = firstMobileCardRef.current;
        const cardHeight = card ? Math.ceil(card.getBoundingClientRect().height) : 0;
        if (cardHeight > 0) {
          viewport.style.height = `${cardHeight + 16}px`;
          viewport.style.flex = "0 0 auto";
        }
        root.style.height = "auto";
        root.style.minHeight = "0px";
        frame = requestAnimationFrame(() => {
          const host = findHost();
          if (!host) return;
          hostRecord = { node: host, height: host.style.height, minHeight: host.style.minHeight };
          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const required = Math.ceil(Math.max(host.scrollHeight,
            Math.max(0, rootRect.top - hostRect.top) + rootRect.height + 8));
          host.style.height = `${required}px`;
          host.style.minHeight = `${required}px`;
        });
      });
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(header);
    if (firstMobileCardRef.current) observer?.observe(firstMobileCardRef.current);
    media.addEventListener?.("change", measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      media.removeEventListener?.("change", measure);
      window.removeEventListener("resize", measure);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [filteredAndSorted.length, visibleKeys]);

  const handleSort = (field) => {
    if (field === sortField) setAscending((current) => !current);
    else {
      setSortField(field);
      setAscending(true);
    }
  };

  const onHeaderDragStart = (event, key) => {
    dragIndexRef.current = visibleKeys.indexOf(key);
    event.dataTransfer.effectAllowed = "move";
  };

  const onHeaderDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onHeaderDrop = (event, dropKey) => {
    event.preventDefault();
    const from = dragIndexRef.current;
    const to = visibleKeys.indexOf(dropKey);
    if (from === null || from < 0 || to < 0 || from === to) return;
    const nextVisible = visibleKeys.slice();
    const [moved] = nextVisible.splice(from, 1);
    nextVisible.splice(to, 0, moved);
    const hidden = order.filter((key) => !visibilityMap[key]);
    setOrder([...nextVisible, ...hidden]);
    dragIndexRef.current = null;
  };

  const resetToDefault = () => {
    setVisibilityMap(makeDefaultVisibility());
    setOrder(makeDefaultOrder());
    setSortField("task_id");
    setAscending(true);
  };

  return (
    <div ref={widgetRootRef} className="h-auto min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col relative md:h-full">
      <div ref={mobileHeaderRef} className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between md:items-center">
        <span className="text-sm font-semibold">
          {mineOnly ? "Search My Tasks" : "Search the Advanced Task List"}
        </span>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:flex md:w-auto md:items-center">
          <input
            type="text"
            className="min-h-[44px] min-w-0 w-full rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:w-72 md:flex-none md:px-2 md:py-1"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tasks..."
          />
          <select
            className="min-h-[44px] rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:px-2 md:py-1"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All statuses</option>
            {statuses.map((value) => (
              <option key={value} value={value}>
                {String(value).replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setChooserOpen(true)}
            className="min-h-[44px] whitespace-nowrap bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow md:min-h-0 md:py-1"
            title="Choose Columns"
          >
            Column Chooser
          </button>
          <button
            type="button"
            onClick={load}
            className="min-h-[44px] whitespace-nowrap bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow md:min-h-0 md:py-1"
          >
            Refresh
          </button>
        </div>

      </div>

      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 bg-accent px-3 pb-3 text-white md:hidden">
        <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold">
          Sort by
          <select
            value={sortField}
            onChange={(event) => {
              setSortField(event.target.value);
              setAscending(true);
            }}
            className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm font-normal text-black"
          >
            {ALL_COLUMNS.map((column) => (
              <option key={column.key} value={column.key}>
                {column.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => setAscending((current) => !current)}
          className="min-h-[44px] self-end rounded bg-btnGeneralNormal px-4 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
          aria-label={ascending ? "Sort descending" : "Sort ascending"}
          title={ascending ? "Currently ascending — tap for descending" : "Currently descending — tap for ascending"}
        >
          {ascending ? "▲" : "▼"}
        </button>
      </div>

      {error && (
        <div className="m-3 rounded-lg border border-red-300 bg-red-50 text-red-800 p-3 text-sm">
          {String(error)}
        </div>
      )}

      {loading ? (
        <div className="p-4 text-sm opacity-60">Loading tasks…</div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto md:flex-1">
          <div className="hidden md:block">
            <table className="w-full text-left text-sm text-gray-700">
              <thead className="sticky top-0 z-10 bg-accent text-white">
                <tr>
                  {visibleKeys.map((key) => {
                    const column = ALL_COLUMNS.find((candidate) => candidate.key === key);
                    if (!column) return null;
                    return (
                      <th
                        key={key}
                        className="px-4 py-2 text-left whitespace-nowrap cursor-move select-none bg-accent"
                        draggable
                        onDragStart={(event) => onHeaderDragStart(event, key)}
                        onDragOver={onHeaderDragOver}
                        onDrop={(event) => onHeaderDrop(event, key)}
                        onClick={() => handleSort(key)}
                        title="Drag to reorder • Click to sort"
                      >
                        {column.label}
                        {sortField === key ? (ascending ? " ▲" : " ▼") : ""}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.map((row) => (
                  <tr
                    key={`${row.item_type}-${row.task_id || row.id}`}
                    onClick={() => openTask(row)}
                    className="h-[41px] border-b border-gray-100 odd:bg-gray-50 even:bg-white hover:bg-gray-100 cursor-pointer"
                  >
                    {visibleKeys.map((key) => (
                      <td key={key} className="px-4 py-2 whitespace-nowrap" title={String(getCellValue(row, key) || "")}>
                        {getCellValue(row, key) || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div ref={mobileViewportRef} className="md:hidden p-2 space-y-2 overflow-y-auto overflow-x-hidden overscroll-contain snap-y snap-mandatory text-gray-700">
            {filteredAndSorted.map((row, index) => (
              <button
                type="button"
                key={`${row.item_type}-mobile-${row.task_id || row.id}`}
                ref={index === 0 ? firstMobileCardRef : undefined}
                onClick={() => openTask(row)}
                className="block w-full text-left rounded-xl border border-gray-200 p-3 bg-white text-gray-700 shadow-sm snap-start scroll-mt-2"
              >
                {visibleKeys.map((key) => {
                  const column = ALL_COLUMNS.find((candidate) => candidate.key === key);
                  if (!column) return null;
                  return (
                    <div key={key} className="flex items-start justify-between gap-4 border-b last:border-b-0 py-2 first:pt-0 last:pb-0">
                      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        {column.label}
                      </span>
                      <span className="min-w-0 break-words text-right text-gray-800">
                        {getCellValue(row, key) || "—"}
                      </span>
                    </div>
                  );
                })}
                <div className="text-right mt-3 text-sm font-medium text-accent">
                  Open ›
                </div>
              </button>
            ))}
          </div>

          {!filteredAndSorted.length && (
            <div className="p-5 text-center text-sm opacity-60">
              No runtime tasks match these filters.
            </div>
          )}
        </div>
      )}

      <ColumnChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
        resetToDefault={resetToDefault}
      />
    </div>
  );
};

export default AdvancedTaskListWidget;
